import React, { useState, useEffect, useCallback } from 'react';
import {
  ActionIcon,
  Alert,
  Anchor,
  Avatar,
  Badge,
  Button,
  Group,
  HoverCard,
  Loader,
  Menu,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import {
  Ban,
  CircleCheck,
  Download,
  EllipsisVertical,
  KeyRound,
  MapPin,
  Pencil,
  Plus,
  Search,
  Trash2,
  UserRound,
  Users as UsersIcon,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout.tsx';
import {
  companiesAPI,
  employeeAPI,
  regionAPI,
  territoriesAPI,
  type EmployeeListFilters,
} from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import {
  ClientBadge,
  ConfirmModal,
  EmptyState,
  PageHeader,
  StatusBadge,
  TablePagination,
} from '../components/app/index.ts';
import { toast } from '../lib/toast.ts';
import { useUrlState } from '../hooks/useUrlState.ts';

const PAGE_SIZE = 100;

const CLIENT_OPTIONS = [
  { value: 'app', label: 'App' },
  { value: 'old_app', label: 'Old app' },
  { value: 'web', label: 'Web' },
  { value: 'old_web', label: 'Old web' },
  { value: 'none', label: 'Never seen' },
];

const ROLE_OPTIONS = [
  { value: 'region_manager', label: 'Regional Manager' },
  { value: 'territory_manager', label: 'Territory Manager' },
  { value: 'field', label: 'Field staff' },
];

// How many territory chips to show before the rest fold into a "+N" that
// reveals them on hover.
const TERRITORIES_SHOWN = 2;

/** An employee's region and territories; many territories fold into a hover. */
const TerritoryRegionCell: React.FC<{ region?: any; territories?: any[] }> = ({
  region,
  territories,
}) => {
  const names = (territories || [])
    .map((territory) => territory?.name)
    .filter(Boolean);
  const shown = names.slice(0, TERRITORIES_SHOWN);
  const rest = names.slice(TERRITORIES_SHOWN);
  const regionName = region?.name;

  if (!regionName && names.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }

  return (
    <Stack gap={4}>
      {regionName && (
        <Group gap={4} wrap="nowrap">
          <MapPin
            size={13}
            aria-hidden="true"
            style={{ opacity: 0.6, flexShrink: 0 }}
          />
          <Text size="xs" fw={600}>
            {regionName}
          </Text>
        </Group>
      )}
      {names.length > 0 ? (
        <Group gap={4} wrap="wrap">
          {shown.map((name) => (
            <Badge key={name} size="sm" variant="light" color="gray">
              {name}
            </Badge>
          ))}
          {rest.length > 0 && (
            <HoverCard withArrow position="top" shadow="md" openDelay={80}>
              <HoverCard.Target>
                <Badge
                  size="sm"
                  variant="outline"
                  color="gray"
                  style={{ cursor: 'pointer' }}
                >
                  +{rest.length}
                </Badge>
              </HoverCard.Target>
              <HoverCard.Dropdown onClick={(event) => event.stopPropagation()}>
                <Group gap={4} maw={280}>
                  {rest.map((name) => (
                    <Badge key={name} size="sm" variant="light" color="gray">
                      {name}
                    </Badge>
                  ))}
                </Group>
              </HoverCard.Dropdown>
            </HoverCard>
          )}
        </Group>
      ) : (
        <Text size="xs" c="dimmed">
          No territories
        </Text>
      )}
    </Stack>
  );
};

/** The app/web build an employee uses, as badges. */
const ClientCell: React.FC<{ clients?: { app?: any; web?: any } }> = ({
  clients,
}) => {
  const app = clients?.app;
  const web = clients?.web;
  if (!app && !web) {
    return (
      <Text size="xs" c="dimmed">
        Never seen
      </Text>
    );
  }
  return (
    <Group gap={4} wrap="wrap">
      {app && <ClientBadge client="app" legacy={app.legacy} size="sm" />}
      {web && <ClientBadge client="web" legacy={web.legacy} size="sm" />}
    </Group>
  );
};

export const Users: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string>('');
  // Kept in the address, so the list comes back as it was left.
  const [page, setPage] = useUrlState('page', 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loadingStatus, setLoadingStatus] = useState<number | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    employeeId: number | null;
  }>({
    isOpen: false,
    employeeId: null,
  });
  const [statusConfirmation, setStatusConfirmation] = useState<{
    isOpen: boolean;
    employeeId: number | null;
    currentStatus: number;
  }>({
    isOpen: false,
    employeeId: null,
    currentStatus: 0,
  });
  const [searchTerm, setSearchTerm] = useUrlState('search', '', {
    resetPage: true,
  });
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);
  const [statusFilter, setStatusFilter] = useUrlState('status', '', {
    resetPage: true,
  });
  const [clientFilter, setClientFilter] = useUrlState('client', '', {
    resetPage: true,
  });
  const [roleFilter, setRoleFilter] = useUrlState('role', '', {
    resetPage: true,
  });
  const [regionFilter, setRegionFilter] = useUrlState('region', '', {
    resetPage: true,
  });
  const [territoryFilter, setTerritoryFilter] = useUrlState('territory', '', {
    resetPage: true,
  });
  const [companyFilter, setCompanyFilter] = useUrlState('company', '', {
    resetPage: true,
  });

  // Options for the dropdowns. Regions/territories are company-scoped on the
  // server; territories are narrowed to the chosen region.
  const [regions, setRegions] = useState<{ value: string; label: string }[]>(
    [],
  );
  const [territories, setTerritories] = useState<
    { value: string; label: string }[]
  >([]);
  const [companies, setCompanies] = useState<
    { value: string; label: string }[]
  >([]);

  const anyFilter =
    searchTerm ||
    statusFilter ||
    clientFilter ||
    roleFilter ||
    regionFilter ||
    territoryFilter ||
    companyFilter;

  // The filters sent to the list and the CSV export, kept in one place.
  const filters: EmployeeListFilters = {
    search: debouncedSearch,
    status: statusFilter,
    client: clientFilter,
    role: roleFilter,
    region: regionFilter,
    territory: territoryFilter,
    company_id: isSuperAdmin ? companyFilter : '',
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Load the region list once (and companies, for a super admin).
  useEffect(() => {
    regionAPI
      .getAll()
      .then((response) => {
        if (response?.success) {
          setRegions(
            (response.data?.regions || []).map((region: any) => ({
              value: String(region.id),
              label: region.name,
            })),
          );
        }
      })
      .catch(() => setRegions([]));

    if (isSuperAdmin) {
      companiesAPI
        .getAll()
        .then((response) => {
          if (response?.success) {
            setCompanies(
              (response.data?.companies || []).map((company: any) => ({
                value: String(company.id),
                label: company.name,
              })),
            );
          }
        })
        .catch(() => setCompanies([]));
    }
  }, [isSuperAdmin]);

  // Territories depend on the chosen region. Clearing the region clears both.
  useEffect(() => {
    if (!regionFilter) {
      setTerritories([]);
      if (territoryFilter) setTerritoryFilter('');
      return;
    }
    let cancelled = false;
    territoriesAPI
      .getByRegionId(Number(regionFilter))
      .then((response) => {
        if (cancelled) return;
        // A 404 (no territories for the region) comes back as success:false.
        const list = response?.success ? response.data || [] : [];
        setTerritories(
          list.map((territory: any) => ({
            value: String(territory.id),
            label: territory.name,
          })),
        );
      })
      .catch(() => {
        if (!cancelled) setTerritories([]);
      });
    return () => {
      cancelled = true;
    };
    // territoryFilter is intentionally excluded: we only refetch on region change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionFilter]);

  const resetFilters = () => {
    setSearchTerm('');
    setDebouncedSearch('');
    setStatusFilter('');
    setClientFilter('');
    setRoleFilter('');
    setRegionFilter('');
    setTerritoryFilter('');
    setCompanyFilter('');
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      await employeeAPI.exportCsv(filters);
      toast.success('CSV downloaded');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await employeeAPI.getAll(page, PAGE_SIZE, {
        search: debouncedSearch,
        status: statusFilter,
        client: clientFilter,
        role: roleFilter,
        region: regionFilter,
        territory: territoryFilter,
        company_id: isSuperAdmin ? companyFilter : '',
      });
      if (response.success) {
        setUsers(
          response.data.employees.sort((a: any, b: any) => {
            return (
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );
          })
        );

        setTotalPages(response.data.pagination.pages);
        setTotalItems(response.data.pagination.total);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [
    page,
    debouncedSearch,
    statusFilter,
    clientFilter,
    roleFilter,
    regionFilter,
    territoryFilter,
    companyFilter,
    isSuperAdmin,
  ]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleDelete = async (id: number) => {
    try {
      setError('');
      const response = await employeeAPI.delete(id);
      if (response.success) {
        toast.success(response.message);
        fetchUsers();
      }
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setDeleteConfirmation({ isOpen: false, employeeId: null });
    }
  };

  const handleToggleStatus = async (id: number) => {
    try {
      setLoadingStatus(id);
      const response = await employeeAPI.toggleStatus(id);
      if (response.success) {
        toast.success(response.message);
        fetchUsers();
      }
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoadingStatus(null);
      // The status is kept so the wording does not flip while the modal fades.
      setStatusConfirmation((current) => ({
        ...current,
        isOpen: false,
        employeeId: null,
      }));
    }
  };


  const closeStatusConfirmation = () =>
    setStatusConfirmation((current) => ({
      ...current,
      isOpen: false,
      employeeId: null,
    }));

  const deactivating = statusConfirmation.currentStatus === 1;

  return (
    <Layout>
      <PageHeader
        title="Users"
        actions={
          <Group gap="sm">
            <Button
              variant="default"
              leftSection={<Download size={16} />}
              loading={exporting}
              onClick={handleExport}
              disabled={loading && users.length === 0}
            >
              Download CSV
            </Button>
            <Button
              component={Link}
              to="/employees/new"
              leftSection={<Plus size={16} />}
            >
              Add User
            </Button>
          </Group>
        }
      />

      <Paper withBorder shadow="sm" p={{ base: 'md', sm: 'lg' }}>
        <Stack gap="md">
          <Stack gap="sm">
            <Group gap="sm" wrap="wrap" align="flex-end">
              <TextInput
                type="search"
                flex={1}
                miw={220}
                placeholder="Search by name, email, phone, pin..."
                leftSection={<Search size={16} />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.currentTarget.value)}
              />
              {anyFilter && (
                <Button color="red" variant="light" onClick={resetFilters}>
                  Reset
                </Button>
              )}
            </Group>
            <Group gap="sm" wrap="wrap">
              {isSuperAdmin && (
                <Select
                  aria-label="Company"
                  placeholder="All companies"
                  w={{ base: '100%', xs: 200 }}
                  data={companies}
                  value={companyFilter || null}
                  onChange={(value) => setCompanyFilter(value || '')}
                  searchable
                  clearable
                />
              )}
              <Select
                aria-label="Status"
                placeholder="All statuses"
                w={{ base: '100%', xs: 160 }}
                data={[
                  { value: '1', label: 'Active' },
                  { value: '0', label: 'Inactive' },
                ]}
                value={statusFilter || null}
                onChange={(value) => setStatusFilter(value || '')}
                clearable
              />
              <Select
                aria-label="Client"
                placeholder="Any client"
                w={{ base: '100%', xs: 150 }}
                data={CLIENT_OPTIONS}
                value={clientFilter || null}
                onChange={(value) => setClientFilter(value || '')}
                clearable
              />
              <Select
                aria-label="Role"
                placeholder="Any role"
                w={{ base: '100%', xs: 180 }}
                data={ROLE_OPTIONS}
                value={roleFilter || null}
                onChange={(value) => setRoleFilter(value || '')}
                clearable
              />
              <Select
                aria-label="Region"
                placeholder="Any region"
                w={{ base: '100%', xs: 180 }}
                data={regions}
                value={regionFilter || null}
                onChange={(value) => setRegionFilter(value || '')}
                searchable
                clearable
              />
              <Select
                aria-label="Territory"
                placeholder={
                  regionFilter ? 'Any territory' : 'Select a region first'
                }
                w={{ base: '100%', xs: 200 }}
                data={territories}
                value={territoryFilter || null}
                onChange={(value) => setTerritoryFilter(value || '')}
                disabled={!regionFilter || territories.length === 0}
                searchable
                clearable
              />
            </Group>
          </Stack>

          {error && (
            <Alert color="red" variant="light">
              <Group justify="space-between" gap="sm">
                <Text size="sm">{error}</Text>
                <Button
                  size="compact-sm"
                  variant="subtle"
                  onClick={() => {
                    setError('');
                    fetchUsers();
                  }}
                >
                  Try again
                </Button>
              </Group>
            </Alert>
          )}

          {/* User List */}
          {loading ? (
            <Group justify="center" h={160}>
              <Loader />
            </Group>
          ) : users.length === 0 ? (
            <EmptyState
              icon={<UsersIcon size={28} />}
              title="No users found"
              description={
                anyFilter
                  ? 'Try adjusting your search or filters.'
                  : 'Get started by adding a new user.'
              }
            />
          ) : (
            <Table.ScrollContainer minWidth={1040}>
              <Table withTableBorder highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>ID</Table.Th>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Email</Table.Th>
                    <Table.Th>Phone</Table.Th>
                    <Table.Th>Territory / Region</Table.Th>
                    <Table.Th>Client</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th ta="right">Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {users.map((user) => (
                    <Table.Tr
                      key={user.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/employees/${user.id}`)}
                    >
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {user.id}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="sm" wrap="nowrap">
                          <Avatar name={user.name} color="initials" size={32} />
                          <Anchor
                            component={Link}
                            to={`/employees/${user.id}`}
                            c="inherit"
                            fw={500}
                            size="sm"
                            onClick={(event) => event.stopPropagation()}
                          >
                            {user.name}
                          </Anchor>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {user.work_email}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {user.mobile_phone}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <TerritoryRegionCell
                          region={user.region}
                          territories={user.territories}
                        />
                      </Table.Td>
                      <Table.Td>
                        <ClientCell clients={user.clients} />
                      </Table.Td>
                      <Table.Td>
                        <StatusBadge
                          status={user.status === 1 ? 'Active' : 'Inactive'}
                        />
                      </Table.Td>
                      {/* The menu must not also open the profile. */}
                      <Table.Td onClick={(event) => event.stopPropagation()}>
                        <Group justify="flex-end">
                          <Menu position="bottom-end" withinPortal>
                            <Menu.Target>
                              <ActionIcon
                                variant="subtle"
                                color="gray"
                                aria-label={`Actions for ${user.name}`}
                              >
                                <EllipsisVertical size={18} />
                              </ActionIcon>
                            </Menu.Target>
                            <Menu.Dropdown>
                              <Menu.Item
                                component={Link}
                                to={`/employees/${user.id}`}
                                leftSection={<UserRound size={16} />}
                              >
                                View profile
                              </Menu.Item>
                              <Menu.Item
                                component={Link}
                                to={`/employees/${user.id}/edit`}
                                leftSection={<Pencil size={16} />}
                              >
                                Edit
                              </Menu.Item>
                              <Menu.Item
                                component={Link}
                                to={`/employees/${user.id}/permissions`}
                                leftSection={<KeyRound size={16} />}
                              >
                                Permissions
                              </Menu.Item>
                              <Menu.Divider />
                              <Menu.Item
                                disabled={loadingStatus === user.id}
                                leftSection={
                                  user.status === 1 ? (
                                    <Ban size={16} />
                                  ) : (
                                    <CircleCheck size={16} />
                                  )
                                }
                                onClick={() =>
                                  setStatusConfirmation({
                                    isOpen: true,
                                    employeeId: user.id,
                                    currentStatus: user.status,
                                  })
                                }
                              >
                                {user.status === 1 ? 'Deactivate' : 'Activate'}
                              </Menu.Item>
                              <Menu.Item
                                color="red"
                                leftSection={<Trash2 size={16} />}
                                onClick={() =>
                                  setDeleteConfirmation({
                                    isOpen: true,
                                    employeeId: user.id,
                                  })
                                }
                              >
                                Delete
                              </Menu.Item>
                            </Menu.Dropdown>
                          </Menu>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}

          {/* Pagination */}
          {users.length > 0 && (
            <TablePagination
              page={page}
              totalPages={totalPages}
              onChange={setPage}
              totalItems={totalItems}
              pageSize={PAGE_SIZE}
            />
          )}
        </Stack>
      </Paper>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        opened={deleteConfirmation.isOpen}
        onClose={() =>
          setDeleteConfirmation({ isOpen: false, employeeId: null })
        }
        onConfirm={() => {
          if (deleteConfirmation.employeeId) {
            handleDelete(deleteConfirmation.employeeId);
          }
        }}
        title="Delete User"
        confirmLabel="Delete"
        color="red"
      >
        Are you sure you want to delete this user? This action cannot be
        undone.
      </ConfirmModal>

      {/* Status Confirmation Modal */}
      <ConfirmModal
        opened={statusConfirmation.isOpen}
        onClose={closeStatusConfirmation}
        onConfirm={() => {
          if (statusConfirmation.employeeId) {
            handleToggleStatus(statusConfirmation.employeeId);
          }
        }}
        title={`${deactivating ? 'Deactivate' : 'Activate'} User`}
        confirmLabel={deactivating ? 'Deactivate' : 'Activate'}
        color={deactivating ? 'red' : 'green'}
        loading={loadingStatus !== null}
      >
        Are you sure you want to {deactivating ? 'deactivate' : 'activate'}{' '}
        this user?
      </ConfirmModal>
    </Layout>
  );
};
